import { Navigate, createFileRoute, useNavigate } from '@tanstack/react-router'

import type { DateRangePresetKey } from '@/components/common/filters/date-range-presets'

import { useDashboardRole } from '@/components/dashboard/use-dashboard-role'
import { ManagerDashboard } from '@/components/dashboard/manager-dashboard'
import { SalesDashboard } from '@/components/dashboard/sales-dashboard'
import { WarehouseDashboard } from '@/components/dashboard/warehouse-dashboard'
import { DriverDashboard } from '@/components/dashboard/driver-dashboard'
import { GrowerDashboard } from '@/components/dashboard/grower-dashboard'
import { LedgerDashboard } from '@/components/dashboard/ledger-dashboard'
import { FallbackDashboard } from '@/components/dashboard/fallback-dashboard'

// Kỳ đang xem của dashboard quản lý. Nằm trên URL để F5 / gửi link / bấm back
// đều giữ đúng kỳ (URL là nguồn sự thật). Kỳ mặc định là tháng này nên không
// ghi vào URL — vào màn thì địa chỉ vẫn sạch.
const DASHBOARD_PERIODS = [
    'today',
    'yesterday',
    'this_week',
    'month',
    '30d',
    'year',
] as const satisfies readonly DateRangePresetKey[]

const DEFAULT_PERIOD: DateRangePresetKey = 'month'

function parsePeriod(raw: unknown): DateRangePresetKey | undefined {
    // Giá trị lạ (link cũ, người dùng sửa tay) thì bỏ qua, rơi về mặc định —
    // không đẩy chuỗi rác xuống API.
    const allowed: readonly DateRangePresetKey[] = DASHBOARD_PERIODS
    return allowed.includes(raw as DateRangePresetKey)
        ? (raw as DateRangePresetKey)
        : undefined
}

export const Route = createFileRoute('/_app/_authed/_sidebar/')({
    // Optional (`?`) + undefined when unset: the router never writes
    // ?kioskExit=false back into the URL, and links to `/` don't have to
    // spell out a search object.
    validateSearch: (
        search: Record<string, unknown>,
    ): { kioskExit?: true; period?: DateRangePresetKey } => ({
        kioskExit:
            search.kioskExit === true ||
            search.kioskExit === 'true' ||
            search.kioskExit === '1' ||
            undefined,
        period:
            parsePeriod(search.period) === DEFAULT_PERIOD
                ? undefined
                : parsePeriod(search.period),
    }),
    component: DashboardPage,
})

/**
 * Home route dispatcher. Renders the dashboard matching the current user's
 * permission capabilities (see resolveDashboard for the tie-break order), or
 * redirects kiosk devices to their dedicated screen. Waits for permissions to
 * load before resolving so a privileged user never flashes the fallback.
 */
function DashboardPage() {
    const { kioskExit, period } = Route.useSearch()
    const navigate = useNavigate({ from: '/' })
    const resolution = useDashboardRole({ allowKioskRedirect: !kioskExit })

    if (resolution.kind === 'loading') {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin h-8 w-8 border-2 border-muted-foreground border-t-transparent rounded-full" />
            </div>
        )
    }

    if (resolution.kind === 'redirect') {
        return <Navigate to={resolution.to} />
    }

    switch (resolution.dashboard) {
        case 'manager':
            return (
                <ManagerDashboard
                    period={period ?? DEFAULT_PERIOD}
                    periods={DASHBOARD_PERIODS}
                    onPeriodChange={(next) =>
                        void navigate({
                            search: (prev) => ({
                                ...prev,
                                period:
                                    next === DEFAULT_PERIOD ? undefined : next,
                            }),
                        })
                    }
                />
            )
        case 'sales':
            return <SalesDashboard />
        case 'warehouse':
            return <WarehouseDashboard />
        case 'driver':
            return <DriverDashboard />
        case 'grower':
            return <GrowerDashboard />
        case 'ledger':
            return <LedgerDashboard />
        case 'fallback':
            return <FallbackDashboard />
    }
}
